import React, { useEffect, useState } from "react";
import Seo from "../../../Components/SeoComponent/Seo";
import Header from "../../../Components/Header";
import { dinToContact } from "../../../Services/ApiTestService/apiTestService";
import { fetchProfile } from "../../../Services/ApiTestService/aadharTestService";
import { useService } from "../../../context/ServiceContext";
import { showErrorToast, showSuccessToast } from "../../../utils/toastUtils";
import {
  CheckIcon,
  XCircle,
  Loader,
  Copy,
  ListCheck,
  FileJson,
} from "lucide-react";
import isNumeric from "validator/lib/isNumeric";

function DinToContact2() {
  const { serviceName } = useService();

  const [din, setDin] = useState("");
  const [consent, setConsent] = useState(false);
  const [dinError, setDinError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isJsonCopied, setIsJsonCopied] = useState(false);

  const [profileData, setProfileData] = useState({
    user_id: "",
    api_key: "",
    token_id: "",
  });

  const [response, setResponse] = useState(null);

  // ------------------ FETCH PROFILE ------------------
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setIsLoading(true);
        const res = await fetchProfile();
        setProfileData(res?.data);
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileData();
  }, []);

  const handleCopy = (data) => {
    navigator.clipboard
      .writeText(JSON.stringify(data, null, 2))
      .then(() => showSuccessToast("Copied!"));

    setIsJsonCopied(true);
  };

  const validateNumber = (value) => {
    if (!value) return false;
    return isNumeric(value, { no_symbols: true });
  };

  const handleContactToDin = async () => {
    if (!din) {
      setDinError("⚠️ Please enter DIN");
      return;
    }
    setDinError("");

    const validate = validateNumber(din);

    if (!validate) {
      setDinError("⚠️ Please enter only numbers");
      return;
    }

    try {
      setIsLoading(true);
      setDinError(null);

      const res = await dinToContact({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        din,
        consent: consent ? "Y" : "N",
      });

      // IMPORTANT: store only res.data
      if (res) {
        setResponse(res);

        // console.log("==============");
        // console.log(res);
        // console.log("====================");
      }

      if (res?.status?.code === 200) {
        showSuccessToast(res?.message || "request successful");
      } else if (res?.status?.code === 422) {
        showErrorToast(response?.data?.message || "Invalid format");
      } else if (res?.status?.code === 500) {
        showErrorToast(res?.data?.message || "server error");
      } else if (!res.success) {
        showErrorToast(res?.data?.message || "verificaton failed");
      } else {
        showErrorToast(response?.data?.message || "something went wrong");
      }

      setDin("");
      setConsent(false);
    } catch (error) {
      console.error(error);
      showErrorToast("Request failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Seo />
      <Header heading={serviceName} />

      {isLoading && <Loader />}

      <div className="w-full min-h-screen flex flex-col items-center py-14">
        {/* ------------------ FORM ------------------ */}
        <div className="flex flex-wrap gap-6">
          <div className="flex flex-wrap gap-4 px-6 justify-start items-center">
            <label className="font-bold">
              DIN <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              value={din}
              maxLength={10}
              onChange={(e) => setDin(e.target.value)}
              className="border border-blue-600 rounded-md px-2 h-[35px]"
              placeholder="Eg: 08993393"
            />

            <div className="flex flex-nowrap gap-2 items-center">
              <label className="font-bold ">
                Consent <span className="text-red-600">*</span>
              </label>
              <input
                type="checkbox"
                checked={consent}
                onChange={() => setConsent((prev) => !prev)}
              />
            </div>

            <button
              disabled={!consent}
              onClick={handleContactToDin}
              className="ml-4 px-4 py-2 bg-gradient-to-r rounded-sm from-blue-400 to-blue-700 text-white disabled:opacity-50"
            >
              Verify
            </button>
          </div>

          {dinError && <p className="text-red-600 font-bold">{dinError}</p>}
          
        </div>

        {response && (
            <div
              className={` ${response?.status?.code === 200 ? "text-green-600" : "text-red-600"} py-6 text-2xl font-bold`}
            >
              {response?.data?.message || response?.message}
            </div>
          )}

        {!response && (
          <div>
            <div className="bg-gray-200 border border-gray-400 p-5 mt-12 rounded-md">
              <h3 className="font-bold text-lg mb-2">
                Important Instructions <span className="text-red-600">*</span>
              </h3>

              <p className="text-sm leading-relaxed">
                Please enter a valid DIN number using only numeric characters.
                Do not include alphabets, spaces, or special characters.
                Providing consent is mandatory to proceed with the verification
                request, so ensure the consent checkbox is selected before
                clicking the Verify button. After successful verification, the
                system will return the response in structured format, including
                contact details associated with the provided DIN. You may review
                and copy the data for your reference or further use.
              </p>
            </div>
          </div>
        )}

        {response?.status?.code === 200 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8 w-full">
            {/* -------- JSON VIEW -------- */}
            <div className="bg-gray-900 relative text-green-300 p-4 rounded-md overflow-auto">
              <button
                onClick={() => handleCopy(response)}
                className="absolute text-white top-4 right-8"
              >
                {isJsonCopied ? "copied" : <Copy />}
              </button>
              <p className="font-bold mb-2 text-2xl flex items-center justify-start md:justify-center gap-2 text-white">
                <FileJson /> JSON response
              </p>
              <hr className="mb-8" />{" "}
              <pre className="text-[16px]">
                {JSON.stringify(response, null, 2)}
              </pre>
            </div>

            {/* user details */}

            <div className="flex flex-col gap-3 rounded-md bg-gray-300 border border-gray-800 p-4 text-black">
              <h2 className="text-2xl flex items-center justify-center gap-2  font-bold text-blue-500 mb-1">
                <ListCheck /> All Details
              </h2>
              <hr className="mb-8" />

              <div className="flex gap-2">
                <span className="font-semibold text-gray-800 w-24">DIN:</span>
                <span className="text-gray-900">
                  {response?.data?.contact_details?.din || "N/A"}
                </span>
              </div>

              <div className="flex gap-2">
                <span className="font-semibold text-gray-800 w-24">
                  Mobile:
                </span>
                <span className="text-gray-900">
                  {response?.data?.contact_details?.mobile || "N/A"}
                </span>
              </div>

              <div className="flex gap-2">
                <span className="font-semibold text-gray-800 w-24">Email:</span>
                <span className="text-gray-900 break-all">
                  {response?.data?.contact_details?.email || "N/A"}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default DinToContact2;
